import type { Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, ledgerKey } from '../http/keys.ts'
import { queryFingerprint } from '../http/query.ts'

export type CrossLedgerLinkKind = Schemas['CrossLedgerLinkResponse']['kind']
export type CrossLedgerLinkDirection = Schemas['CrossLedgerLinkResponse']['direction']

/** Which of this ledger's own entries is one side of a cross-ledger movement, and its counterpart. */
export interface CrossLedgerLink {
  readonly entryId: string
  readonly counterpartLedgerId: string
  readonly counterpartLedgerName: string
  readonly counterpartEntryId: string
  readonly kind: CrossLedgerLinkKind
  readonly direction: CrossLedgerLinkDirection
}

export const crossLedgerLinkKeys = {
  list: (ledgerId: string, entryIds: readonly string[]): CacheKey =>
    ledgerKey(ledgerId, 'cross-ledger-links', queryFingerprint({ entryIds: [...entryIds].sort() })),
} as const

export interface CrossLedgerLinksRepository {
  /** Batched per visible page of entries - never one call per row. */
  list(
    ledgerId: string,
    entryIds: readonly string[],
    signal?: AbortSignal,
  ): Promise<readonly CrossLedgerLink[]>
}

export function createCrossLedgerLinksRepository(client: TalerClient): CrossLedgerLinksRepository {
  return {
    async list(ledgerId, entryIds, signal) {
      if (entryIds.length === 0) return []
      const response = await client.call<Schemas['CrossLedgerLinkResponse'][]>({
        method: 'GET',
        path: `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/cross-ledger-links`,
        query: { entryIds: [...entryIds] },
        ...(signal === undefined ? {} : { signal }),
      })
      return response.map(toCrossLedgerLink)
    },
  }
}

function toCrossLedgerLink(response: Schemas['CrossLedgerLinkResponse']): CrossLedgerLink {
  return {
    entryId: response.entryId,
    counterpartLedgerId: response.counterpartLedgerId,
    counterpartLedgerName: response.counterpartLedgerName,
    counterpartEntryId: response.counterpartEntryId,
    kind: response.kind,
    direction: response.direction,
  }
}
